import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
// ==========================================================================
// ASYNC THUNKS
// ==========================================================================
export const fetchAffiliateProjection = createAsyncThunk("affiliateProjection/fetch", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/affiliate-projection/view", payload, {
            Authorization: token,
            "x-module-key": "affiliate_account_performance",
        });
        const responseData = response?.data;
        if (responseData?.success) {
            return responseData.data;
        }
        return rejectWithValue(responseData?.message || "Failed to fetch affiliate projection");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching affiliate projection");
    }
});
// ==========================================================================
// INITIAL STATE
// ==========================================================================
const getDefaultMonth = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
};
const initialState = {
    clients: [],
    totalSummary: null,
    filters: [],
    pagination: null,
    lastUpdatedAt: null,
    overallStatus: null,
    loading: false,
    error: null,
    month: getDefaultMonth(),
    accountManagerId: "",
    clientType: "View All",
    totalGoalToggle: false,
    page: 1,
    limit: 100, // Backend caps this endpoint's limit at 100 (affiliateprojectioncontroller.ts) — matches that ceiling so every active affiliate client loads on one page; pagination controls are hidden by design.
    sortBy: "affiliateGoal",
    sortOrder: "desc",
    refreshLoading: false,
    refreshError: null,
    refreshResult: null,
};
// ==========================================================================
// SLICE
// ==========================================================================
const affiliateProjectionSlice = createSlice({
    name: "affiliateProjection",
    initialState,
    reducers: {
        setMonth: (state, action) => {
            state.month = action.payload;
            state.page = 1;
        },
        setAccountManagerId: (state, action) => {
            state.accountManagerId = action.payload;
            state.page = 1;
        },
        setClientType: (state, action) => {
            state.clientType = action.payload;
            state.page = 1;
        },
        setTotalGoalToggle: (state, action) => {
            state.totalGoalToggle = action.payload;
            state.page = 1;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setLimit: (state, action) => {
            state.limit = action.payload;
            state.page = 1;
        },
        setSortConfig: (state, action) => {
            state.sortBy = action.payload.sortBy;
            state.sortOrder = action.payload.sortOrder;
            state.page = 1;
        },
        clearAffiliateProjection: (state) => {
            state.clients = [];
            state.totalSummary = null;
            state.filters = [];
            state.pagination = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchAffiliateProjection.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchAffiliateProjection.fulfilled, (state, action) => {
            state.loading = false;
            state.clients = action.payload.clients || [];
            state.totalSummary = action.payload.total_summary || null;
            state.filters = action.payload.filters || [];
            state.pagination = action.payload.pagination || null;
            state.lastUpdatedAt = action.payload.lastUpdatedAt || null;
            state.overallStatus = action.payload.overall_status || "not_found";
            state.error = null;
        })
            .addCase(fetchAffiliateProjection.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch affiliate projection";
        });
    },
});
// ==========================================================================
// EXPORTS
// ==========================================================================
export const { setMonth, setAccountManagerId, setClientType, setTotalGoalToggle, setPage, setLimit, setSortConfig, clearAffiliateProjection, } = affiliateProjectionSlice.actions;
// Selectors
export const selectAffiliateProjectionClients = (state) => state.affiliateProjection.clients;
export const selectAffiliateProjectionLoading = (state) => state.affiliateProjection.loading;
export const selectAffiliateProjectionError = (state) => state.affiliateProjection.error;
export const selectAffiliateProjectionTotalSummary = (state) => state.affiliateProjection.totalSummary;
export const selectAffiliateProjectionFilters = (state) => state.affiliateProjection.filters;
export const selectAffiliateProjectionPagination = (state) => state.affiliateProjection.pagination;
export const selectAffiliateProjectionLastUpdatedAt = (state) => state.affiliateProjection.lastUpdatedAt;
export const selectAffiliateProjectionOverallStatus = (state) => state.affiliateProjection.overallStatus;
export const selectAffiliateProjectionRefreshLoading = (state) => state.affiliateProjection.refreshLoading;
export default affiliateProjectionSlice.reducer;
